import { uploadThemeMedia } from "../../functions/uploadThemeMedia";
import { renameFileForUpload } from "./safeUploadFileName";

const IMAGE_ONLY_MAX_MB = 5;
const IMAGE_ONLY_EXT = ["jpg", "jpeg", "png"];
const IMAGE_ONLY_MIME = ["image/jpeg", "image/jpg", "image/png"];

export const IMAGE_ONLY_REQUIRED_WIDTH = 300;
export const IMAGE_ONLY_REQUIRED_HEIGHT = 180;
export const IMAGE_ONLY_DIMENSION_LABEL = `${IMAGE_ONLY_REQUIRED_WIDTH}x${IMAGE_ONLY_REQUIRED_HEIGHT}px`;

export const IMAGE_ONLY_FIELD = "image_only_image";

export { renameFileForUpload };

function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image file."));
    };
    img.src = url;
  });
}

export async function validateImageOnlyImageFile(file) {
  if (!file) return { ok: false, message: "Please choose an image file." };

  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (!IMAGE_ONLY_EXT.includes(ext)) {
    return {
      ok: false,
      message: `Unsupported format for ${file.name}. Only PNG or JPG images are allowed.`,
    };
  }

  const mime = (file.type || "").toLowerCase();
  if (mime && !IMAGE_ONLY_MIME.includes(mime)) {
    return {
      ok: false,
      message: `Unsupported format for ${file.name}. Only PNG or JPG images are allowed.`,
    };
  }

  if (file.size > IMAGE_ONLY_MAX_MB * 1024 * 1024) {
    return {
      ok: false,
      message: `${file.name} exceeds ${IMAGE_ONLY_MAX_MB}MB.`,
    };
  }

  try {
    const { width, height } = await getImageDimensions(file);
    if (width !== IMAGE_ONLY_REQUIRED_WIDTH || height !== IMAGE_ONLY_REQUIRED_HEIGHT) {
      return {
        ok: false,
        message: `The dimension should be ${IMAGE_ONLY_DIMENSION_LABEL}. Your image is ${width}x${height}px.`,
      };
    }
  } catch {
    return {
      ok: false,
      message: "Failed to validate image dimensions. Please choose a valid PNG or JPG file.",
    };
  }

  return { ok: true };
}

export async function uploadImageOnlyImageFile({ file, adminToken, currentTheme }) {
  const check = await validateImageOnlyImageFile(file);
  if (!check.ok) throw new Error(check.message);

  const renamed = renameFileForUpload(file, "image");
  const formData = new FormData();
  formData.append("themeName", currentTheme);
  formData.append("uploadOnly", "true");
  formData.append(IMAGE_ONLY_FIELD, renamed);

  const data = await uploadThemeMedia({ formData, token: adminToken });
  const filename = data?.uploaded_files?.[IMAGE_ONLY_FIELD];
  if (!filename) {
    throw new Error("Image upload failed — no filename returned from server.");
  }
  return filename;
}
